/* eslint-disable */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi } from "@/lib/adminApi";
import { Card } from "@/components/admin/_shared";

export default function AdminMonitorTab() {
  const [metrics, setMetrics] = useState(null);
  const [health, setHealth] = useState(null);
  const [activity, setActivity] = useState(null);
  const [ceiling, setCeiling] = useState("");
  const [security, setSecurity] = useState(null);
  const [dataHealth, setDataHealth] = useState(null);

  const load = async () => {
    const m = await adminApi.get("/admin/system/metrics");
    setMetrics(m.data);
    setCeiling(String(m.data.monthly_cost_ceiling));
    const h = await adminApi.get("/admin/system/health");
    setHealth(h.data);
    const a = await adminApi.get("/admin/system/activity");
    setActivity(a.data);
    try {
      const s = await adminApi.get("/admin/security/events");
      setSecurity(s.data);
    } catch {
      /* security events are best-effort */
    }
    try {
      const dh = await adminApi.get("/admin/data-health/events");
      setDataHealth(dh.data);
    } catch {
      /* data-health events are best-effort */
    }
  };
  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(load, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(t);
  }, []);

  const saveCeiling = async () => {
    const n = parseFloat(ceiling);
    if (isNaN(n) || n <= 0) return toast.error("Invalid ceiling");
    await adminApi.post("/admin/system/ceiling", { monthly_cost_ceiling: n });
    toast.success("Ceiling updated");
    await load();
  };

  if (!metrics) return <div className="text-[var(--rg-text)]">Loading metrics…</div>;
  const burn = metrics.burn_rate;
  const bandColor = burn.band === "pending"
    ? "var(--rg-muted)"
    : { green: "var(--rg-sage)", yellow: "var(--rg-amber)", red: "var(--rg-red)" }[burn.band];

  return (
    <div className="space-y-6" data-testid="admin-monitor-tab">
      {burn.band === "red" && (
        <div className="rounded-md border border-[var(--rg-red)] bg-[var(--rg-red-soft)] px-4 py-3 text-sm text-[var(--rg-text-strong)]" data-testid="admin-burn-alert">
          <AlertTriangle className="w-4 h-4 inline mr-2 text-[var(--rg-red)]" />
          Burn rate alert: projected spend of ${burn.projected_total} exceeds your ${burn.ceiling} ceiling.
        </div>
      )}

      {burn.projection_available === false && (
        <div className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage)]/10 px-4 py-3 text-sm text-[var(--rg-text-strong)]" data-testid="admin-projection-pending">
          <AlertTriangle className="w-4 h-4 inline mr-2 text-[var(--rg-sage)]" />
          {burn.projection_pending_message || "Projection available after 3 days of data. Check back on day 3."}
          {" "}
          <span className="text-[var(--rg-text)] text-xs">(Day {burn.days_elapsed} of month so far.)</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="API calls (this month)" value={metrics.api_calls_this_month} />
        <Card label="API calls (all time)" value={metrics.api_calls_total_ever} />
        <Card label="Cost this month" value={`$${metrics.api_cost_this_month.toFixed(2)}`} />
        <Card label="Cache hits" value={metrics.cache_hits_this_month} />
        <Card label="Failed calls" value={metrics.failed_calls_this_month} />
        <Card label="Daily average" value={`$${burn.daily_average.toFixed(2)}`} />
        <Card
          label="Projected total"
          value={burn.projection_available ? `$${burn.projected_total.toFixed(2)}` : "—"}
          valueColor={bandColor}
          testId="admin-projected-total"
        />
        <Card label="% of ceiling" value={burn.projection_available ? `${burn.percent_of_ceiling}%` : "—"} valueColor={bandColor} />
      </div>

      <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4">
        <Label className="text-[var(--rg-text)] text-xs uppercase">Monthly cost ceiling (USD)</Label>
        <div className="flex gap-2 mt-1 items-center">
          <Input value={ceiling} onChange={(e) => setCeiling(e.target.value)} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-xs" data-testid="admin-ceiling-input" />
          <Button size="sm" onClick={saveCeiling} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="admin-ceiling-save">Update</Button>
        </div>
      </div>

      {health && (
        <div data-testid="admin-health-probes">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-bold text-[var(--rg-text-strong)]">Per-service Health Probes</h3>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)]">
              Auto-refresh every 5 min · Last check {health.as_of?.slice(11, 19)} UTC
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { k: "Database", ok: health.database_ok },
              { k: "Claude API", ok: health.claude_api_ok },
              { k: "Lemon Squeezy", ok: health.lemonsqueezy_ok },
              { k: "Resend", ok: health.resend_ok, mocked: health.resend_mocked },
            ].map((p) => (
              <div key={p.k} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 flex items-center justify-between" data-testid={`health-${p.k.toLowerCase().replace(/\s/g, '-')}`}>
                <span className="text-sm text-[var(--rg-text-strong)]">
                  {p.k}
                  {p.mocked && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-[var(--rg-amber)] font-bold">MOCKED</span>}
                </span>
                <span className={p.ok ? "text-[var(--rg-sage)] text-xs font-bold uppercase" : "text-[var(--rg-red)] text-xs font-bold uppercase"}>
                  {p.ok ? "Healthy" : "Down"}
                </span>
              </div>
            ))}
          </div>
          <LsVariantsCard variants={health.ls_variants} />
        </div>
      )}

      <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4">
        <h3 className="font-heading font-bold text-[var(--rg-text-strong)] mb-2">Recent activity</h3>
        <div className="text-xs text-[var(--rg-text)] space-y-1 max-h-80 overflow-y-auto">
          {(activity?.audits || []).slice(0, 12).map((a, i) => (
            <div key={i}><span className="font-mono text-[var(--rg-muted)]">{(a.audit_date || "").slice(0, 19)}</span> · audit · score {a.composite_score}</div>
          ))}
          {(activity?.changes || []).slice(0, 8).map((c, i) => (
            <div key={"c" + i}><span className="font-mono text-[var(--rg-muted)]">{(c.created_at || "").slice(0, 19)}</span> · change · {c.collection}::{c.abbreviation}</div>
          ))}
        </div>
      </div>

      {/* ---- Task 1 (2026-06-25) — Data Health Events ---- */}
      <PilotScoringHealthCard />
      <ExtractorSandboxCard />
      <SystemMaintenanceCard onDone={load} />
      <div
        className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4"
        data-testid="admin-data-health-events"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-[var(--rg-text-strong)] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[var(--rg-amber)]" /> Data Health Events
          </h3>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)]">
            Startup migrations · admin purges · document deletions
          </span>
        </div>
        {(dataHealth?.events || []).length === 0 ? (
          <div className="text-xs text-[var(--rg-muted)] italic" data-testid="data-health-empty">
            No data-health events recorded. This is the healthy state.
          </div>
        ) : (
          <div className="space-y-2" data-testid="data-health-events-list">
            {dataHealth.events.map((e, i) => {
              const isAmber = (e.deleted_count || 0) > 0;
              return (
                <div
                  key={e.id || i}
                  className={`rounded-md border p-3 text-sm ${
                    isAmber
                      ? "border-[var(--rg-amber)] bg-[var(--rg-amber-soft)]"
                      : "border-[var(--rg-border)] bg-[var(--rg-surface)]"
                  }`}
                  data-testid={`data-health-event-${i}`}
                >
                  <div className="flex flex-wrap justify-between items-baseline gap-2">
                    <div className="font-heading font-bold text-[var(--rg-text-strong)] text-sm uppercase tracking-wide">
                      {e.event || "unknown_event"}
                    </div>
                    <div className={`font-mono text-xs ${isAmber ? "text-[var(--rg-amber)]" : "text-[var(--rg-text)]"}`}>
                      deleted: <span className="font-bold text-base">{e.deleted_count ?? 0}</span>
                    </div>
                  </div>
                  <div className="text-[10px] text-[var(--rg-muted)] font-mono mt-1">
                    {(e.timestamp || "").slice(0, 19)}
                    {e.triggered_by ? ` · by ${e.triggered_by}` : ""}
                  </div>
                  {Array.isArray(e.details) && e.details.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-[11px] text-[var(--rg-text)] cursor-pointer hover:text-[var(--rg-text-strong)]">
                        View {e.details.length} affected document{e.details.length === 1 ? "" : "s"}
                      </summary>
                      <div className="mt-1 max-h-40 overflow-y-auto rounded bg-[var(--rg-bg)] border border-[var(--rg-border)] p-2 font-mono text-[10px] text-[var(--rg-text)]">
                        {e.details.map((d, k) => (
                          <div key={k} className="truncate">{String(d)}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ---- Item 2 — Security Events ---- */}
      <div
        className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4"
        data-testid="admin-security-events"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-[var(--rg-text-strong)] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[var(--rg-amber)]" /> Security Events
          </h3>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)]">
            Failed admin logins · account lockouts
          </span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3">
            <div className="text-[10px] uppercase tracking-wider text-[var(--rg-muted)]">Recent failed logins</div>
            <div
              className="text-2xl font-bold text-[var(--rg-text-strong)]"
              data-testid="security-failed-count"
            >
              {security?.summary?.failed_login_count_recent ?? 0}
            </div>
          </div>
          <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3">
            <div className="text-[10px] uppercase tracking-wider text-[var(--rg-muted)]">Lockout events</div>
            <div
              className="text-2xl font-bold text-[var(--rg-amber)]"
              data-testid="security-lockout-count"
            >
              {security?.summary?.lockout_count_recent ?? 0}
            </div>
          </div>
          <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3">
            <div className="text-[10px] uppercase tracking-wider text-[var(--rg-muted)]">Currently locked accounts</div>
            <div
              className="text-2xl font-bold text-[var(--rg-red)]"
              data-testid="security-active-locks-count"
            >
              {security?.summary?.active_locks?.length ?? 0}
            </div>
          </div>
        </div>
        {security?.summary?.active_locks?.length > 0 && (
          <div className="mb-3 rounded-md border border-[var(--rg-red)] bg-[var(--rg-red-soft)] p-3 text-xs text-[var(--rg-text-strong)]" data-testid="security-active-locks-list">
            {security.summary.active_locks.map((l) => (
              <div key={l.email} className="font-mono">
                <span className="text-[var(--rg-red)] font-bold">LOCKED</span>{" "}
                {l.email}{" "}
                {l.locked_until_iso && (
                  <span className="text-[var(--rg-text)]">until {(l.locked_until_iso || "").slice(11, 19)} UTC</span>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="text-xs text-[var(--rg-text)] space-y-1 max-h-72 overflow-y-auto font-mono" data-testid="security-events-list">
          {security?.events?.length ? (
            security.events.map((e, i) => (
              <div key={i} className={e.type === "lockout" ? "text-[var(--rg-red)]" : "text-[var(--rg-text)]"}>
                <span className="text-[var(--rg-muted)]">{(e.at || "").slice(0, 19)}</span>
                {" · "}
                <span className="uppercase font-bold">{e.type.replace("_", " ")}</span>
                {" · "}
                {e.email}
                {e.ip ? ` · ${e.ip}` : ""}
                {e.count ? ` · attempt #${e.count}` : ""}
              </div>
            ))
          ) : (
            <div className="text-[var(--rg-muted)] italic">No recent security events.</div>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * LsVariantsCard — surfaces the daily Lemon Squeezy variant health probe.
 * Green "LS Variants OK" pill when all 4 active variants respond 200 with the
 * expected id. Red per-variant callout when any returns an error or a mismatch.
 * The daily job writes to system_metrics; this component reads the cached
 * result exposed by /admin/system/health. "Recheck now" fires an on-demand
 * ping via /admin/system/ls-variants-check-now.
 */
function LsVariantsCard({ variants }) {
  const [rechecking, setRechecking] = useState(false);

  const recheck = async () => {
    try {
      setRechecking(true);
      await adminApi.post("/admin/system/ls-variants-check-now");
      toast.success("LS variant check complete");
      // AdminMonitorTab's parent poller refreshes every 5 min; give the parent
      // hook a nudge by dispatching a synthetic event that the outer effect
      // listens to. Falls back to page reload if that pattern isn't wired.
      window.dispatchEvent(new CustomEvent("regulens:admin-monitor-reload"));
      setTimeout(() => window.location.reload(), 400);
    } catch {
      toast.error("Recheck failed");
    } finally {
      setRechecking(false);
    }
  };

  if (!variants || variants.not_run_yet) {
    return (
      <div
        className="mt-3 rounded-md border border-[var(--rg-muted)] bg-[var(--rg-surface)] p-3 flex items-center justify-between"
        data-testid="ls-variants-status"
      >
        <span className="text-sm text-[var(--rg-text-strong)]">
          LS Variants
          <span className="ml-1.5 text-[9px] uppercase tracking-wider text-[var(--rg-muted)] font-bold">PENDING</span>
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[var(--rg-muted)] text-xs font-bold uppercase">First check pending</span>
          <button
            type="button"
            onClick={recheck}
            disabled={rechecking}
            className="text-[10px] uppercase tracking-wider text-[var(--rg-sage)] hover:text-[var(--rg-text-strong)] border border-[var(--rg-border)] rounded-sm px-2 py-0.5"
            data-testid="ls-variants-recheck-btn"
          >
            {rechecking ? "…" : "Recheck now"}
          </button>
        </div>
      </div>
    );
  }

  const allOk = variants.all_ok;
  const results = variants.results || [];
  const failed = results.filter((r) => !r.ok);
  const checkedAt = variants.checked_at ? String(variants.checked_at).slice(0, 19).replace("T", " ") : "—";

  return (
    <div className="mt-3 space-y-2" data-testid="ls-variants-status">
      <div
        className={`rounded-md border p-3 flex items-center justify-between ${
          allOk ? "border-[var(--rg-sage)] bg-[var(--rg-sage)]/5" : "border-[var(--rg-red)] bg-[var(--rg-red-soft)]"
        }`}
      >
        <span className="text-sm text-[var(--rg-text-strong)] flex items-center gap-2">
          LS Variants
          <span className="text-[10px] text-[var(--rg-muted)] font-mono">last checked {checkedAt}</span>
        </span>
        <div className="flex items-center gap-3">
          <span
            className={`text-xs font-bold uppercase ${allOk ? "text-[var(--rg-sage)]" : "text-[var(--rg-red)]"}`}
            data-testid={allOk ? "ls-variants-ok" : "ls-variants-error"}
          >
            {allOk ? "LS Variants OK" : `${failed.length} of ${results.length} FAILED`}
          </span>
          <button
            type="button"
            onClick={recheck}
            disabled={rechecking}
            className="text-[10px] uppercase tracking-wider text-[var(--rg-sage)] hover:text-[var(--rg-text-strong)] border border-[var(--rg-border)] rounded-sm px-2 py-0.5"
            data-testid="ls-variants-recheck-btn"
          >
            {rechecking ? "…" : "Recheck now"}
          </button>
        </div>
      </div>
      {!allOk && failed.map((r) => (
        <div
          key={r.variant_id}
          className="rounded-md border border-[var(--rg-red)] bg-[var(--rg-red-soft)] p-3 text-sm"
          data-testid={`ls-variant-fail-${r.variant_id}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[var(--rg-text-strong)] font-semibold">{r.label || r.tier}</span>
            <span className="font-mono text-[11px] text-[var(--rg-amber)]">id: {r.variant_id}</span>
          </div>
          <div className="text-[12px] text-[var(--rg-text)] mt-1">
            {r.error || "Unknown error"}{r.http_status ? ` (HTTP ${r.http_status})` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}


/**
 * SystemMaintenanceCard — one-shot corrective actions that touch merchant
 * data across the whole DB. Each action follows the same pattern used
 * elsewhere in the app: dry-run preview → typed confirmation → apply.
 * Emits a data_health_events row for every action.
 */
function SystemMaintenanceCard({ onDone }) {
  const [preview, setPreview] = useState(null); // legacy Pillar-2 preview
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/audits/legacy-pillar2-preview");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!preview || preview.count === 0) return;
    const noun = preview.count === 1 ? "audit" : "audits";
    const msg = `Re-score ${preview.count} ${noun} with legacy Pillar-2 = 100?\n\n${
      sendEmail
        ? "Each affected merchant will receive an email notifying them of the correction."
        : "No email will be sent — merchants will only see the corrected score when they next open their dashboard."
    }`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/audits/rescore-legacy-pillar2", {
        confirm: true, send_email: sendEmail,
      });
      toast.success(
        `Corrected ${data.count} audit(s)` +
        (sendEmail ? ` · ${data.emails_sent} email(s) sent` : ""),
      );
      setPreview(null);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rescore failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4" data-testid="admin-system-maintenance">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-bold text-[var(--rg-text-strong)] flex items-center gap-2">
          System Maintenance
        </h3>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)]">
          One-shot corrective sweeps
        </span>
      </div>
      <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[var(--rg-text-strong)] font-semibold text-sm">Rescore Legacy Pillar-2</div>
            <div className="text-[12px] text-[var(--rg-text)] mt-1 leading-snug">
              Finds audits where <code className="font-mono">pillar2 == 100</code> because of the pre-fix fallback,
              re-runs scoring with today's rules (yielding <code>N/A</code> when no state applies), and
              recalculates the composite. Optional: email each affected merchant.
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {!preview && (
              <Button
                size="sm"
                onClick={runPreview}
                disabled={loading}
                className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold"
                data-testid="rescore-legacy-preview"
              >
                {loading ? "Loading…" : "Preview affected audits"}
              </Button>
            )}
            {preview && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPreview(null)}
                disabled={busy}
                className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-border)]/40"
                data-testid="rescore-legacy-reset"
              >
                Reset
              </Button>
            )}
          </div>
        </div>

        {preview && (
          <div className="mt-3 pt-3 border-t border-[var(--rg-border)]">
            <div className="flex items-baseline gap-4 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)]">Affected audits</div>
                <div className="text-2xl font-heading font-bold text-[var(--rg-text-strong)]" data-testid="rescore-legacy-count">
                  {preview.count}
                </div>
              </div>
              {preview.count > 0 && (
                <label className="flex items-center gap-2 text-sm text-[var(--rg-text)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    data-testid="rescore-legacy-send-email"
                    className="w-4 h-4"
                  />
                  Send correction notice email to each merchant
                </label>
              )}
            </div>
            {preview.count === 0 ? (
              <div className="mt-3 text-xs text-[var(--rg-muted)] italic" data-testid="rescore-legacy-empty">
                No historical audits need correction. Nothing to do.
              </div>
            ) : (
              <>
                <div className="mt-3 max-h-52 overflow-y-auto rounded border border-[var(--rg-border)]">
                  <table className="w-full text-[12px]">
                    <thead className="text-[10px] uppercase tracking-wider text-[var(--rg-muted)] bg-[var(--rg-bg)]">
                      <tr>
                        <th className="text-left px-2 py-1.5">Email</th>
                        <th className="text-left px-2 py-1.5">Old composite</th>
                        <th className="text-left px-2 py-1.5">States</th>
                        <th className="text-left px-2 py-1.5">Last audit</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono text-[11px] text-[var(--rg-text)]">
                      {preview.hits.map((h, i) => (
                        <tr key={h.profile_id || i} className="border-t border-[var(--rg-border)]" data-testid={`rescore-legacy-row-${i}`}>
                          <td className="px-2 py-1.5">{h.email || "—"}</td>
                          <td className="px-2 py-1.5">{h.old_composite ?? "—"} / 100</td>
                          <td className="px-2 py-1.5">{(h.selected_jurisdictions || []).join(", ") || "—"}</td>
                          <td className="px-2 py-1.5">{(h.last_audit_date || "").slice(0, 10) || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    onClick={runApply}
                    disabled={busy}
                    className="bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold"
                    data-testid="rescore-legacy-apply"
                  >
                    {busy ? "Applying…" : `Rescore ${preview.count} audit(s)${sendEmail ? " + email" : ""}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * PilotScoringHealthCard — Tab 6 (Stage 4 / Prompt B) impact projection.
 *
 * Read-only tile that answers "if I flip CA/VA/CO to ACTIVE right now,
 * what actually happens to my real merchant base?" Data is projected from
 * the live scoring engine — no writes, no cache invalidation.
 */
function PilotScoringHealthCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activateResult, setActivateResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await adminApi.get("/admin/pilot-scoring/health");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load pilot health");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const anyPending = (data?.pilots || []).some((p) => p.scoring_policy === "PENDING_REVIEW");
  const allActive  = (data?.pilots || []).length > 0
                     && (data?.pilots || []).every((p) => p.scoring_policy === "ACTIVE");

  const runActivation = async () => {
    setActivating(true);
    setActivateResult(null);
    setErr(null);
    try {
      const r = await adminApi.post("/admin/prompt-b/activate-pilot");
      setActivateResult(r.data);
      toast.success(`Activated ${r.data.activated_count} state(s). Refreshing…`);
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Activation failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setActivating(false);
      setConfirming(false);
    }
  };

  const policyBadge = (p) => {
    const map = {
      ACTIVE:         { color: "var(--rg-sage)", label: "ACTIVE" },
      PENDING_REVIEW: { color: "var(--rg-amber)", label: "PENDING REVIEW" },
      DISABLED:       { color: "var(--rg-muted)", label: "DISABLED" },
    };
    const m = map[p] || { color: "var(--rg-muted)", label: p || "—" };
    return (
      <span
        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
        style={{ color: m.color, borderColor: m.color, border: "1px solid" }}
      >
        {m.label}
      </span>
    );
  };

  return (
    <div
      className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4"
      data-testid="admin-pilot-scoring-health"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-[var(--rg-text-strong)] flex items-center gap-2">
          Pilot Scoring Health — Prompt B
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)]">
            Projection: what if activated today
          </span>
          <Button
            size="sm"
            onClick={refresh}
            disabled={loading}
            className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)] text-xs h-7"
            data-testid="pilot-health-refresh"
          >
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </div>

      {err && (
        <div className="text-xs text-[var(--rg-red)]" data-testid="pilot-health-error">{err}</div>
      )}

      {!err && !data && (
        <div className="text-xs text-[var(--rg-muted)] italic">Loading pilot state impact…</div>
      )}

      {!err && data && (
        <div className="grid md:grid-cols-3 gap-3" data-testid="pilot-health-grid">
          {(data.pilots || []).map((p) => {
            const proj = p.projection_if_activated || {};
            const dist = p.score_distribution || {};
            const total = (proj.scored || 0) + (proj.below_threshold || 0) + (proj.provisional || 0);
            return (
              <div
                key={p.abbreviation}
                className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3"
                data-testid={`pilot-health-${p.abbreviation}`}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[var(--rg-text-strong)] font-heading font-bold text-lg">
                    {p.abbreviation}
                    <span className="ml-2 text-[10px] font-normal text-[var(--rg-muted)]">
                      weight ×{p.enforcement_weight ?? "—"}
                    </span>
                  </div>
                  {policyBadge(p.scoring_policy)}
                </div>

                <div className="text-[11px] text-[var(--rg-text)] mb-2">
                  <span className="text-[var(--rg-text-strong)] font-bold" data-testid={`pilot-selectors-${p.abbreviation}`}>
                    {p.merchants_selecting_state}
                  </span>{" "}
                  merchant{p.merchants_selecting_state === 1 ? "" : "s"} selected this state ·{" "}
                  <span className="text-[var(--rg-text-strong)] font-bold">
                    {p.new_signals_complete_selectors}
                  </span>{" "}
                  have completed the 3 new signals
                </div>

                <div className="mt-3 border-t border-[var(--rg-border)] pt-2">
                  <div className="text-[9px] uppercase tracking-widest text-[var(--rg-muted)] mb-1.5">
                    If activated today
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between" data-testid={`pilot-scored-${p.abbreviation}`}>
                      <span className="text-[var(--rg-sage)]">Scored</span>
                      <span className="text-[var(--rg-text-strong)] font-bold">{proj.scored || 0}</span>
                    </div>
                    <div className="flex justify-between" data-testid={`pilot-below-${p.abbreviation}`}>
                      <span className="text-[var(--rg-muted)]">Below threshold</span>
                      <span className="text-[var(--rg-text-strong)] font-bold">{proj.below_threshold || 0}</span>
                    </div>
                    <div className="flex justify-between" data-testid={`pilot-provisional-${p.abbreviation}`}>
                      <span className="text-[var(--rg-amber)]">Provisional / needs signals</span>
                      <span className="text-[var(--rg-text-strong)] font-bold">{proj.provisional || 0}</span>
                    </div>
                  </div>
                </div>

                {(proj.scored || 0) > 0 && (
                  <div className="mt-3 border-t border-[var(--rg-border)] pt-2">
                    <div className="text-[9px] uppercase tracking-widest text-[var(--rg-muted)] mb-1.5">
                      Score distribution (scored merchants)
                    </div>
                    <div className="flex gap-1 h-2 rounded overflow-hidden bg-[var(--rg-bg)]" data-testid={`pilot-dist-bar-${p.abbreviation}`}>
                      <div
                        className="bg-[var(--rg-red)]"
                        style={{ width: `${((dist["0-49"] || 0) / proj.scored) * 100}%` }}
                        title={`0-49: ${dist["0-49"] || 0}`}
                      />
                      <div
                        className="bg-[var(--rg-amber)]"
                        style={{ width: `${((dist["50-79"] || 0) / proj.scored) * 100}%` }}
                        title={`50-79: ${dist["50-79"] || 0}`}
                      />
                      <div
                        className="bg-[var(--rg-sage)]"
                        style={{ width: `${((dist["80-100"] || 0) / proj.scored) * 100}%` }}
                        title={`80-100: ${dist["80-100"] || 0}`}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-[var(--rg-muted)] mt-1 font-mono">
                      <span>0-49: {dist["0-49"] || 0}</span>
                      <span>50-79: {dist["50-79"] || 0}</span>
                      <span>80-100: {dist["80-100"] || 0}</span>
                    </div>
                  </div>
                )}

                {total === 0 && p.merchants_selecting_state > 0 && (
                  <div className="mt-2 text-[10px] text-[var(--rg-muted)] italic">
                    No projection buckets populated — check merchant data.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!err && data && (
        <div className="mt-3 text-[10px] text-[var(--rg-muted)] italic">
          Read-only projection using the live scoring engine (v3). No merchant data is mutated.
          Provisional = merchant selected the state but hasn't answered the 3 Prompt-B onboarding questions.
        </div>
      )}

      {/* One-click activation footer — only shows if there's at least one
          PENDING_REVIEW state to flip. Idempotent by design. */}
      {!err && data && anyPending && (
        <div
          className="mt-4 border-t border-[var(--rg-border)] pt-3 space-y-2"
          data-testid="prompt-b-activation-footer"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-[var(--rg-text)]">
              <span className="text-[var(--rg-text-strong)] font-semibold">Activate Prompt B pilot</span>
              <span className="text-[var(--rg-muted)]"> — flips any PENDING_REVIEW pilot states to ACTIVE. Already-ACTIVE states are skipped.</span>
            </div>
            {!confirming ? (
              <Button
                size="sm"
                onClick={() => setConfirming(true)}
                disabled={activating}
                className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold text-xs"
                data-testid="prompt-b-activate-btn"
              >
                Activate CA / VA / CO
              </Button>
            ) : (
              <div className="flex gap-2" data-testid="prompt-b-activate-confirm">
                <Button
                  size="sm"
                  onClick={runActivation}
                  disabled={activating}
                  className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)]/85 font-semibold text-xs"
                  data-testid="prompt-b-activate-confirm-yes"
                >
                  {activating ? "Activating…" : "Yes, flip to ACTIVE"}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setConfirming(false)}
                  disabled={activating}
                  className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)] text-xs"
                  data-testid="prompt-b-activate-confirm-no"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
          {confirming && (
            <div className="rounded-md border border-[var(--rg-red)]/60 bg-[var(--rg-red)]/5 px-3 py-2 text-[11px] text-[var(--rg-text)]">
              This will make CA / VA / CO comprehensive privacy scoring live for every merchant. Merchants who selected these states but haven't answered the 3 new onboarding questions will see the Provisional banner. Cache is auto-invalidated by SCORING_ENGINE_VERSION — no quota is consumed. This action is logged in <span className="font-mono text-[var(--rg-amber)]">data_health_events</span>.
            </div>
          )}
        </div>
      )}

      {!err && data && allActive && !activateResult && (
        <div className="mt-4 border-t border-[var(--rg-border)] pt-3 text-[11px] text-[var(--rg-sage)]" data-testid="prompt-b-all-active">
          ✓ All 3 Prompt-B pilot states are ACTIVE.
        </div>
      )}

      {activateResult && (
        <div className="mt-4 border-t border-[var(--rg-border)] pt-3 space-y-1" data-testid="prompt-b-activate-result">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--rg-sage)]">
            Activation result — {activateResult.activated_count} state(s) flipped
          </div>
          {(activateResult.results || []).map((r) => (
            <div key={r.abbreviation} className="text-[11px] font-mono text-[var(--rg-text)] flex gap-2">
              <span className="text-[var(--rg-text-strong)] font-bold">{r.abbreviation}</span>
              <span className="text-[var(--rg-muted)]">{r.previous || "—"} → {r.current || "—"}</span>
              <span className={
                r.outcome === "activated"       ? "text-[var(--rg-sage)]" :
                r.outcome === "already_active"  ? "text-[var(--rg-muted)]" :
                "text-[var(--rg-amber)]"
              }>
                {r.outcome}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * ExtractorSandboxCard — Stage 1 (Session 1) admin-only sandbox for the
 * AI privacy-rights extractor. Paste any policy text and see what the AI
 * returns, side-by-side with the legacy keyword matcher's answers.
 * Never touches merchant data. Never mutates anything except the
 * `privacy_extractions` cache (write-through on AI hits).
 */
function ExtractorSandboxCard() {
  const [status, setStatus] = useState(null);
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const loadStatus = async () => {
    try {
      const r = await adminApi.get("/admin/extractor/status");
      setStatus(r.data);
    } catch { /* best-effort */ }
  };
  useEffect(() => { loadStatus(); }, []);

  const run = async () => {
    setErr(null);
    setResult(null);
    if (!text || text.trim().length < 20) {
      setErr("Please paste at least 20 characters of policy text.");
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/extractor/test-run", {
        policy_text: text,
        use_cache: true,
      });
      setResult(r.data);
      await loadStatus();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Extractor call failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => { setText(""); setResult(null); setErr(null); };

  const badgeFor = (ans) => {
    const map = {
      yes:     { bg: "var(--rg-sage)", fg: "var(--rg-bg)", label: "YES" },
      no:      { bg: "var(--rg-red)", fg: "#FFFFFF", label: "NO" },
      unclear: { bg: "var(--rg-amber)", fg: "var(--rg-bg)", label: "UNCLEAR" },
    };
    const m = map[ans] || { bg: "var(--rg-muted)", fg: "var(--rg-bg)", label: (ans || "—").toUpperCase() };
    return (
      <span
        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
        style={{ background: m.bg, color: m.fg }}
      >
        {m.label}
      </span>
    );
  };

  const sourceLabel = (s) => {
    if (s === "ai")       return { color: "var(--rg-sage)", label: "AI FRESH" };
    if (s === "cache")    return { color: "var(--rg-sage)", label: "CACHE HIT" };
    return                       { color: "var(--rg-amber)", label: "FALLBACK" };
  };

  const legacyBoolFor = (rightName) => {
    if (!result?.keyword_comparison) return null;
    // Only the 4 legacy rights are covered by the keyword matcher
    if (!(rightName in result.keyword_comparison)) return null;
    return result.keyword_comparison[rightName];
  };

  return (
    <div
      className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4"
      data-testid="admin-extractor-sandbox"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading font-bold text-[var(--rg-text-strong)] flex items-center gap-2">
          Privacy Extractor Sandbox
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          {status && (
            <>
              <span className="text-[10px] uppercase tracking-widest text-[var(--rg-muted)]" data-testid="extractor-mode-chip">
                mode: <span className={status.mode === "ai_hybrid" ? "text-[var(--rg-sage)] font-bold" : "text-[var(--rg-amber)] font-bold"}>{status.mode}</span>
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[var(--rg-muted)]">
                cached: <span className="text-[var(--rg-text-strong)] font-bold">{status.cache_entries}</span>
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[var(--rg-muted)]" data-testid="extractor-fallback-7d">
                fallback 7d: <span className={status.ai_fallback_last_7d > 0 ? "text-[var(--rg-amber)] font-bold" : "text-[var(--rg-muted)]"}>{status.ai_fallback_last_7d}</span>
              </span>
            </>
          )}
        </div>
      </div>

      {status?.ai_fallback_last_7d > 0 && (
        <div className="mb-3 rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] px-3 py-2 text-xs text-[var(--rg-text-strong)]" data-testid="extractor-fallback-alert">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 text-[var(--rg-amber)]" />
          <strong>{status.ai_fallback_last_7d}</strong> AI extraction fallback event{status.ai_fallback_last_7d === 1 ? "" : "s"} in the last 7 days.
          The extractor silently fell back to the keyword matcher — see Data Health Events below for reasons.
        </div>
      )}

      <div className="text-[11px] text-[var(--rg-text)] mb-2">
        Paste any privacy-policy text below to see what the AI extractor returns.
        Sandbox always uses Claude (bypasses <code className="text-[var(--rg-sage)]">EXTRACTOR_MODE</code>). No merchant data is touched.
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste the merchant's privacy policy here…"
        className="w-full min-h-[140px] rounded-md bg-[var(--rg-bg)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] text-xs p-3 font-mono resize-y focus:outline-none focus:border-[var(--rg-sage)]"
        data-testid="extractor-sandbox-input"
        disabled={busy}
      />

      <div className="mt-2 flex gap-2 items-center">
        <Button
          size="sm"
          onClick={run}
          disabled={busy || text.trim().length < 20}
          className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold"
          data-testid="extractor-sandbox-run"
        >
          {busy ? "Extracting…" : "Run extractor"}
        </Button>
        <Button
          size="sm"
          onClick={clear}
          disabled={busy}
          className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"
          data-testid="extractor-sandbox-clear"
        >
          Clear
        </Button>
        <span className="text-[10px] text-[var(--rg-muted)] ml-auto">
          {text.length.toLocaleString()} characters
        </span>
      </div>

      {err && (
        <div className="mt-3 text-xs text-[var(--rg-red)]" data-testid="extractor-sandbox-error">{err}</div>
      )}

      {result && (
        <div className="mt-4 space-y-3" data-testid="extractor-sandbox-result">
          <div className="flex items-baseline justify-between border-b border-[var(--rg-border)] pb-2">
            <div className="flex items-center gap-2">
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ background: sourceLabel(result.source).color, color: "var(--rg-bg)" }}
                data-testid="extractor-source-badge"
              >
                {sourceLabel(result.source).label}
              </span>
              {result.model && (
                <span className="text-[10px] font-mono text-[var(--rg-muted)]">{result.model}</span>
              )}
              {result.fallback_reason && (
                <span className="text-[10px] font-mono text-[var(--rg-amber)]">
                  reason: {result.fallback_reason}
                </span>
              )}
            </div>
            <span className="text-[10px] font-mono text-[var(--rg-muted)]">
              hash: {(result.policy_hash || "").slice(0, 16)}…
            </span>
          </div>

          <div className="grid gap-2">
            {(result.rights || []).map((r) => {
              const kw = legacyBoolFor(r.right);
              const aiSaysYes = r.answer === "yes";
              const kwSaysYes = kw === true;
              const disagrees = kw !== null && aiSaysYes !== kwSaysYes;
              return (
                <div
                  key={r.right}
                  className={`rounded-md border p-2.5 ${
                    disagrees ? "border-[var(--rg-amber)] bg-[var(--rg-amber)]/5" : "border-[var(--rg-border)] bg-[var(--rg-surface)]"
                  }`}
                  data-testid={`extractor-right-${r.right}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[var(--rg-text-strong)] font-semibold text-sm">{r.right}</span>
                      {badgeFor(r.answer)}
                      {kw !== null && (
                        <span className="text-[10px] text-[var(--rg-muted)] font-mono">
                          keyword: <span className={kwSaysYes ? "text-[var(--rg-sage)]" : "text-[var(--rg-red)]"}>{kwSaysYes ? "YES" : "NO"}</span>
                        </span>
                      )}
                      {disagrees && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--rg-amber)] text-[var(--rg-on-sage)]">
                          Disagree
                        </span>
                      )}
                    </div>
                  </div>
                  {r.supporting_quote && (
                    <div className="mt-1 text-[11px] text-[var(--rg-text)] italic border-l-2 border-[var(--rg-sage)] pl-2" data-testid={`extractor-quote-${r.right}`}>
                      "{r.supporting_quote}"
                    </div>
                  )}
                  {!r.supporting_quote && r.answer !== "unclear" && (
                    <div className="mt-1 text-[10px] text-[var(--rg-muted)] italic">
                      No supporting quote (fallback answer).
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-[10px] text-[var(--rg-muted)] italic mt-2">
            Amber "Disagree" rows are where the AI answer differs from the legacy keyword matcher —
            these are precisely the cases where the current production scoring may be inaccurate.
          </div>
        </div>
      )}
    </div>
  );
}
